/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import RGBAColor from "./RGBAColor";
import { DEFAULT_QUAD_COLOR } from "../Constants/constants";
import {
  calculateIntersectionPoint,
  findDistanceBwPoints,
  intersectionPointLocationEnum,
  LineSegment
} from "../Utils/GeometryUtils";
import { QUAD_TYPE } from "../Constants/enums";

function sliceArrayInHalf(list) {
  const half = Math.ceil(list.length / 2);

  const firstHalf = list.slice(0, half);
  const secondHalf = list.slice(-half);
  return {
    firstHalf,
    secondHalf
  };
}

class CanvasQuad {
  /**
        ### quadPoints: (default = [])
            
            It is an array of coordinates in the format:
            [x1, y1, x2, y2, x3, y3, x4, y4]
            This is a visual representation of those quadpoints. 
            (x1,y1) .----. (x2,y2)
                    |    |
            (x4,y4) .----. (x3,y3) 
            Basically the array is filled by reading the visual representation in a clockwise order.
            
            This property is used when the quad is a single quad and not a merge of multiple quads.
            To create a merged quad, it is best to use originalQuadPointsArray in the parameters.

        
        ### originalQuadPointsArray: (default = [])
            
            It is an array of quadPoints described above. It is used to create merged quad.
            It can contain one or more quadPoints array. Initially this contains quadPoints of 
            only one quad. But as quad merge operations are done, it gets populated with 
            the original quadPoints of all merged quads.
            
            If only quadPoints array is given, then it is assumed that the quad is a single quad and 
            not a merged quad and originalQuadPointsArray is populated using these quadPoints.

            If both originalQuadPointsArray and quadPoints are given, then quadPoints array is ignored.

        ### type: (default = null)
            
            This property signifies the type of the quad. Currently only search result type 
            (DEF_SEARCH_RESULT and CURR_SEARCH_RESULT) are being handled for high contrast mode.
        
        ### color: (RGBAColor, default = light blue)
            
            This property signifies the color of the quad in the format of RGBAColor class.
    */
  constructor({
    quadPoints = [],
    originalQuadPointsArray = [],
    type = null,
    color = new RGBAColor(
      DEFAULT_QUAD_COLOR.red,
      DEFAULT_QUAD_COLOR.green,
      DEFAULT_QUAD_COLOR.blue,
      DEFAULT_QUAD_COLOR.alpha
    )
  }) {
    this.originalQuadPointsArray = this._getValidQuadsArray(
      originalQuadPointsArray,
      quadPoints
    );
    this.color = color;
    this.type = type;
  }

  _getValidQuadsArray(quadPointsArray, quadPoints) {
    let validQuadPointsArray = [];
    if (CanvasQuad.isQuadPointsArrayValid(quadPointsArray)) {
      validQuadPointsArray = quadPointsArray;
    } else if (CanvasQuad.areQuadPointsValid(quadPoints)) {
      validQuadPointsArray = [quadPoints];
    }
    return validQuadPointsArray;
  }

  static isQuadPointsArrayValid(quadPointsArray) {
    return (
      Array.isArray(quadPointsArray) &&
      quadPointsArray.length > 0 &&
      quadPointsArray.every(CanvasQuad.areQuadPointsValid)
    );
  }

  static areQuadPointsValid(quadPoints) {
    return Array.isArray(quadPoints) && quadPoints.length === 8;
  }

  get topLeft() {
    // eslint-disable-next-line
        const [x1, y1, x2, y2, x3, y3, x4, y4] = this.quadPoints;
    return {
      x: x1,
      y: y1
    };
  }

  get topRight() {
    // eslint-disable-next-line
        const [x1, y1, x2, y2, x3, y3, x4, y4] = this.quadPoints;
    return {
      x: x2,
      y: y2
    };
  }

  get bottomRight() {
    // eslint-disable-next-line
        const [x1, y1, x2, y2, x3, y3, x4, y4] = this.quadPoints;
    return {
      x: x3,
      y: y3
    };
  }

  get bottomLeft() {
    // eslint-disable-next-line
        const [x1, y1, x2, y2, x3, y3, x4, y4] = this.quadPoints;
    return {
      x: x4,
      y: y4
    };
  }

  get left() {
    return Math.min(
      this.topLeft.x,
      this.topRight.x,
      this.bottomLeft.x,
      this.bottomRight.x
    );
  }

  get right() {
    return Math.max(
      this.topLeft.x,
      this.topRight.x,
      this.bottomLeft.x,
      this.bottomRight.x
    );
  }

  get top() {
    return Math.min(
      this.topLeft.y,
      this.topRight.y,
      this.bottomLeft.y,
      this.bottomRight.y
    );
  }

  get bottom() {
    return Math.max(
      this.topLeft.y,
      this.topRight.y,
      this.bottomLeft.y,
      this.bottomRight.y
    );
  }

  get width() {
    const { right, left } = this;
    return Math.abs(right - left);
  }

  get height() {
    const { bottom, top } = this;
    return Math.abs(bottom - top);
  }

  /**
     * The getter returns the angle (degrees) of the left edge of the quad wrt X-axis
     * in anti clockwise direction. 
     * The left edge is decided wrt the reading orientation of the quad.
     * It is the line bw topLeft (x1,y1) and bottomLeft (x4,y4) of the quad.
     * Eg: 
       1) 0 degree rotation of the text. 
          quadpoints = [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
          visual:
            (Ax1,Ay1) .----. (Ax2,Ay2)
                      |    |
            (Ax4,Ay4) .----. (Ax3,Ay3)
            The angle of left edge is 90 wrt X axis.
       
       2) 90 degree rotation of the text. 
           quadpoints = [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
           visual:
            (Ax4,Ay4) .----. (Ax1,Ay1)
                      |    |
            (Ax3,Ay3) .----. (Ax2,Ay2)
            The angle of left edge is 0 wrt X axis.

       3) 180 degree rotation of the text. 
           quadpoints = [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
           visual:
            (Ax3,Ay3) .----. (Ax4,Ay4)
                      |    |
            (Ax2,Ay2) .----. (Ax1,Ay1)
            The angle of left edge is -90 wrt X axis.
       
       4) 270 degree rotation of the text. 
           quadpoints = [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
           visual:
            (Ax2,Ay2) .----. (Ax3,Ay3)
                      |    |
            (Ax1,Ay1) .----. (Ax4,Ay4)
            The angle of left edge is -180 wrt X axis.
     * 
     * This function tells us the exact state of the orientation of the text.
     * @returns {Number} angle in degrees
     */
  get angle() {
    const dy = this.topLeft.y - this.bottomLeft.y;
    const dx = this.topLeft.x - this.bottomLeft.x;
    /* -dy because View coordinate system starts from top left unlike PDF coordinate system which starts from bottom left.
         Hence the point above has lower y than the point below.
         */
    const theta = Math.atan2(-dy, dx);
    return (theta * 180) / Math.PI;
  }

  /**
     * This getter property is calculated by merging the individual quadpoints and
     * returns the top outline bounds of the merged polygon. The reading order of array is clockwise similar to that of the quad. 
     * Example: We have 2 quads 
     * 1) [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
            (Ax1,Ay1) .----. (Ax2,Ay2)
                      |    |
            (Ax4,Ay4) .----. (Ax3,Ay3)
     * 2) [Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4]
            (Bx1,By1) .----. (Bx2,By2)
                      |    |
            (Bx4,By4) .----. (Bx3,By3)
            
        The polygonTopEdgePoints array will be :
        [Ax1, Ay1, Ax2, Ay2, Bx1, By1, Bx2, By2]
            (Ax1,Ay1) .----. (Ax2,Ay2).----.(Bx1,By1) .----. (Bx2,By2)
        @returns {Array}
     */
  get polygonTopEdgePoints() {
    let upperHalf = [];
    const originalQuadPointsArray = this.originalQuadPointsArray.slice();
    if (this.type === QUAD_TYPE.CARET_QUAD) {
      originalQuadPointsArray.pop();
    }
    originalQuadPointsArray.forEach(currentQuadPoints => {
      const { firstHalf } = sliceArrayInHalf(currentQuadPoints);
      upperHalf = upperHalf.concat(firstHalf);
    });
    return upperHalf;
  }

  /**
     * This getter property is calculated by merging the individual quadpoints and
     * returns the bottom outline bounds of the merged polygon. The reading order of array is clockwise similar to that of the quad. 
     * Example: We have 2 quads 
     * 1) [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
            (Ax1,Ay1) .----. (Ax2,Ay2)
                      |    |
            (Ax4,Ay4) .----. (Ax3,Ay3)
     * 2) [Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4]
            (Bx1,By1) .----. (Bx2,By2)
                      |    |
            (Bx4,By4) .----. (Bx3,By3)
            
        The polygonPoints array will be :
        [Bx3, By3, Bx4, By4, Ax3, Ay3, Ax4, Ay4]
            (Ax4,Ay4) .----. (Ax3,Ay3).----.(Bx4,By4) .----. (Bx3,By3)
        @returns {Array}
     */
  get polygonBottomEdgePoints() {
    let lowerHalf = [];
    this.originalQuadPointsArray.forEach(currentQuadPoints => {
      const { secondHalf } = sliceArrayInHalf(currentQuadPoints);
      lowerHalf = secondHalf.concat(lowerHalf);
    });
    return lowerHalf;
  }

  /**
     * This getter property is calculated by merging the individual quadpoints and
     * returns the outline bounds of the merged polygon. The reading order of array is clockwise similar to that of the quad. 
     * Example: We have 2 quads 
     * 1) [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
            (Ax1,Ay1) .----. (Ax2,Ay2)
                      |    |
            (Ax4,Ay4) .----. (Ax3,Ay3)
     * 2) [Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4]
            (Bx1,By1) .----. (Bx2,By2)
                      |    |
            (Bx4,By4) .----. (Bx3,By3)
            
        The polygonPoints array will be :
        [Ax1, Ay1, Ax2, Ay2, Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4, Ax3, Ay3, Ax4, Ay4]
            (Ax1,Ay1) .----. (Ax2,Ay2).----.(Bx1,By1) .----. (Bx2,By2)
                      |                                   |
            (Ax4,Ay4) .----. (Ax3,Ay3).----.(Bx4,By4) .----. (Bx3,By3)
        @returns {Array}
     */
  get polygonPoints() {
    let upperHalf = [];
    let lowerHalf = [];
    this.originalQuadPointsArray.forEach(currentQuadPoints => {
      const { firstHalf, secondHalf } = sliceArrayInHalf(currentQuadPoints);
      upperHalf = upperHalf.concat(firstHalf);
      lowerHalf = secondHalf.concat(lowerHalf);
    });
    const points = upperHalf.concat(lowerHalf);
    return points;
  }

  /**
     * This getter property returns the unified bounding rect of all the quadpoints present in the
     * originalQuadPointsArray. 
        @returns {Object} 
        union =  {
            left,
            top,
            right,
            bottom,
            width,
            height
        }
     */
  get polygonBoundsUnion() {
    let left = Number.MAX_SAFE_INTEGER;
    let right = Number.MIN_SAFE_INTEGER;
    let top = Number.MAX_SAFE_INTEGER;
    let bottom = Number.MIN_SAFE_INTEGER;

    this.originalQuadPointsArray.forEach(currentQuadPoints => {
      const curQuad = new CanvasQuad({ quadPoints: currentQuadPoints });
      left = Math.min(left, curQuad.left);
      right = Math.max(right, curQuad.right);
      top = Math.min(top, curQuad.top);
      bottom = Math.max(bottom, curQuad.bottom);
    });

    return {
      left,
      top,
      right,
      bottom,
      width: Math.abs(right - left),
      height: Math.abs(top - bottom)
    };
  }

  get leftMostQuad() {
    if (this.originalQuadPointsArray.length) {
      return new CanvasQuad({ quadPoints: this.originalQuadPointsArray[0] });
    }
    return null;
  }

  get rightMostQuad() {
    if (this.originalQuadPointsArray.length) {
      return new CanvasQuad({
        quadPoints: this.originalQuadPointsArray[
          this.originalQuadPointsArray.length - 1
        ]
      });
    }
    return null;
  }

  /**
     * This getter property returns the quadpoints of the quad which can be formed after the merging of all the quads
     * in originalQuadPointsArray. The reading order of the final array too is clockwise. 
     * Example: We have 2 quads in the array.
     * 1) [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4]
            (Ax1,Ay1) .----. (Ax2,Ay2)
                      |    |
            (Ax4,Ay4) .----. (Ax3,Ay3)
     * 2) [Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4]
            (Bx1,By1) .----. (Bx2,By2)
                      |    |
            (Bx4,By4) .----. (Bx3,By3)
            
        The quadPoints array will be :
        [Ax1, Ay1, Bx2, By2, Bx3, By3, Ax4, Ay4]
            (Ax1,Ay1) .------------. (Bx2,By2)
                      |            |
            (Ax4,Ay4) .------------. (Bx3,By3)
        @returns {Array}
     */
  get quadPoints() {
    let finalQuadPoints = [];
    const originalQuadPointsArray = this.originalQuadPointsArray.slice();
    if (this.type === QUAD_TYPE.CARET_QUAD) {
      originalQuadPointsArray.pop();
    }
    originalQuadPointsArray.forEach(currentQuadPoints => {
      // eslint-disable-next-line
            let [x1, y1, x2, y2, x3, y3, x4, y4] = currentQuadPoints;
      if (finalQuadPoints.length === 0) {
        finalQuadPoints = currentQuadPoints.slice();
      } else {
        finalQuadPoints.splice(2, 4, x2, y2, x3, y3);
      }
    });
    return finalQuadPoints;
  }

  _translatePointsArray(x, y, pointsArray) {
    const translateFn = (oldX, oldY) => ({
      x: oldX - x,
      y: oldY - y
    });
    return this._transformPointsArray(pointsArray, translateFn);
  }

  _transformPointsArray(pointsArray, transformFn, quadIndex) {
    const transformedPointsArray = [];
    for (let index = 0; index < pointsArray.length - 1; index += 2) {
      const oldX = pointsArray[index];
      const oldY = pointsArray[index + 1];
      const pointIndexInQuad = index / 2;
      const { x: newX, y: newY } = transformFn(
        oldX,
        oldY,
        pointIndexInQuad,
        quadIndex
      );
      transformedPointsArray.push(newX, newY);
    }
    return transformedPointsArray;
  }

  equals(otherQuad) {
    if (
      otherQuad &&
      otherQuad instanceof CanvasQuad &&
      CanvasQuad.isQuadPointsArrayValid(otherQuad.originalQuadPointsArray) &&
      CanvasQuad.isQuadPointsArrayValid(this.originalQuadPointsArray) &&
      otherQuad.originalQuadPointsArray.length ===
        this.originalQuadPointsArray.length
    ) {
      const areQuadPointsEqual = (quadPoints1, quadPoints2) =>
        quadPoints1.every((point, index) => quadPoints2[index] === point);
      return this.originalQuadPointsArray.every((currentQuadPoints, index) => {
        const otherQuadPoints = otherQuad.originalQuadPointsArray[index];
        return areQuadPointsEqual(currentQuadPoints, otherQuadPoints);
      });
    }

    return false;
  }

  updateColor(color) {
    this.color = color;
  }

  updateType(type) {
    this.type = type;
  }

  /**
   * This function transforms the current canvasQuad using the given transform function.
   * The transform function performs operation on given x,y and returns new x,y coordinates.
   * This transformation is applied on each point in the quad and transformed quad is returned in a new object.
   * This function doesn't modify the existing object.
   *
   * @param {Function} transformFn It takes 3 params: ( oldX, oldY, pointIndex)
   * pointIndex: It tells the index of the point (oldX,oldY) in the visual represention of the quad.
   * Eg: 1) [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4],
   *        (Ax1,Ay1) has pointIndex 1, (Ax2,Ay2) has pointIndex 2 and so on.
   * @returns {CanvasQuad} transformed CanvasQuad
   */
  transform(transformFn) {
    const transformedArray = this.originalQuadPointsArray.map(
      (curQuadPoints, quadIndex) =>
        this._transformPointsArray(curQuadPoints, transformFn, quadIndex)
    );
    return new CanvasQuad({
      ...this,
      originalQuadPointsArray: transformedArray,
      type: this.type
    });
  }

  /**
   * This function translates the current canvasQuad using the given pivot point.
   * This translation is applied on each point in the quad and translated quad is returned in a new object.
   * This function doesn't modify the existing object.
   *
   * @param {Number} x x coordinate of the pivot point
   * @param {Number} y y coordinate of the pivot point
   * @returns {CanvasQuad} translated CanvasQuad
   */
  translate(x = 0, y = 0) {
    const translatedArray = this.originalQuadPointsArray.map(curQuadPoints =>
      this._translatePointsArray(x, y, curQuadPoints)
    );
    return new CanvasQuad({
      ...this,
      originalQuadPointsArray: translatedArray
    });
  }

  /**
     * This function merges the current quad with the given quad in arguments
     * and return a new CanvasQuad with the merged quadpoints of both quads.
     * This function doesn't modify the existing object. 
     * 
     * Example: We have 2 CanvasQuad objects with following originalQuadPointsArray 
     * 1) originalQuadPointsArray = [ [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4] ]
            (Ax1,Ay1) .----. (Ax2,Ay2)
                      |    |
            (Ax4,Ay4) .----. (Ax3,Ay3)
     * 2) originalQuadPointsArray = [ [Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4] ]
            (Bx1,By1) .----. (Bx2,By2)
                      |    |
            (Bx4,By4) .----. (Bx3,By3)
            
        The returned object will have originalQuadPointsArray in the following format:
         originalQuadPointsArray = [ [Ax1, Ay1, Ax2, Ay2, Ax3, Ay3, Ax4, Ay4] , [Bx1, By1, Bx2, By2, Bx3, By3, Bx4, By4] ]
        @returns {CanvasQuad} merged CanvasQuad
     */
  mergeQuad(otherQuad) {
    const mergedOriginalQuadPointsArray = this.originalQuadPointsArray
      .slice()
      .concat(otherQuad.originalQuadPointsArray);
    return new CanvasQuad({
      ...this,
      originalQuadPointsArray: mergedOriginalQuadPointsArray
    });
  }

  getEffectiveCaretQuad(otherQuad) {
    const originalQp = this.originalQuadPointsArray.slice();
    const lastQuad = originalQp[originalQp.length - 1];
    const edgePerRotation = this.getEdgesAsPerRotation(
      lastQuad,
      otherQuad.originalQuadPointsArray[0],
      this.angle
    );
    const rightEdgeQuadLineSegment = new LineSegment(
      {
        x: edgePerRotation.rightEdgeQuad[0],
        y: edgePerRotation.rightEdgeQuad[1]
      },
      {
        x: edgePerRotation.rightEdgeQuad[2],
        y: edgePerRotation.rightEdgeQuad[3]
      }
    );

    const topEdgeCaretLineSegment = new LineSegment(
      {
        x: edgePerRotation.topEdgeCaret[0],
        y: edgePerRotation.topEdgeCaret[1]
      },
      { x: edgePerRotation.topEdgeCaret[2], y: edgePerRotation.topEdgeCaret[3] }
    );

    let firstIntersection = calculateIntersectionPoint(
      rightEdgeQuadLineSegment,
      topEdgeCaretLineSegment
    );

    const rightEdgeCaretLineSegment = new LineSegment(
      {
        x: edgePerRotation.rightEdgeCaret[0],
        y: edgePerRotation.rightEdgeCaret[1]
      },
      {
        x: edgePerRotation.rightEdgeCaret[2],
        y: edgePerRotation.rightEdgeCaret[3]
      }
    );

    const bottomEdgeQuadLineSegment = new LineSegment(
      {
        x: edgePerRotation.bottomEdgeQuad[0],
        y: edgePerRotation.bottomEdgeQuad[1]
      },
      {
        x: edgePerRotation.bottomEdgeQuad[2],
        y: edgePerRotation.bottomEdgeQuad[3]
      }
    );

    let secondIntersection = calculateIntersectionPoint(
      rightEdgeCaretLineSegment,
      bottomEdgeQuadLineSegment
    );
    if (!firstIntersection) {
      const caretQuad = otherQuad.originalQuadPointsArray[0];
      firstIntersection = { x: caretQuad[0], y: caretQuad[1] };
    }
    if (!secondIntersection) {
      const caretQuad = otherQuad.originalQuadPointsArray[0];
      secondIntersection = { x: caretQuad[4], y: caretQuad[5] };
    }
    const newCaretQuad = [
      firstIntersection.x,
      firstIntersection.y,
      edgePerRotation.topEdgeCaret[2],
      edgePerRotation.topEdgeCaret[3],
      secondIntersection.x,
      secondIntersection.y,
      edgePerRotation.bottomEdgeQuad[0],
      edgePerRotation.bottomEdgeQuad[1]
    ];
    const mergedQp = originalQp.concat([newCaretQuad]);
    return new CanvasQuad({
      ...this,
      originalQuadPointsArray: mergedQp,
      type: QUAD_TYPE.CARET_QUAD
    });
  }

  getEdgesAsPerRotation(lastQuad, caretQuad) {
    const [x1l, y1l, x2l, y2l, x3l, y3l, x4l, y4l] = lastQuad;
    const [
      x1r,
      y1r,
      x2r,
      y2r,
      x3r,
      y3r,
      x4r,
      y4r
    ] = this.findCaretVerticesInOrder(lastQuad, caretQuad);
    return {
      topEdgeQuad: [x1l, y1l, x2l, y2l],
      rightEdgeQuad: [x2l, y2l, x3l, y3l],
      bottomEdgeQuad: [x3l, y3l, x4l, y4l],
      leftEdgeQuad: [x4l, y4l, x1l, y1l],
      topEdgeCaret: [x1r, y1r, x2r, y2r],
      rightEdgeCaret: [x2r, y2r, x3r, y3r],
      bottomEdgeCaret: [x3r, y3r, x4r, y4r],
      leftEdgeCaret: [x4r, y4r, x1r, y1r]
    };
  }

  findCaretVerticesInOrder(lastQuad, caretQuad) {
    const [x1, y1, x2, y2, x3, y3, x4, y4] = caretQuad;
    // eslint-disable-next-line no-unused-vars
    const [X1, Y1, X2, Y2, X3, Y3, X4, Y4] = lastQuad;
    const rightEdgeQuadLineSegment = new LineSegment(
      {
        x: X2,
        y: Y2
      },
      {
        x: X3,
        y: Y3
      }
    );
    const firstEdgeCaret = new LineSegment(
      {
        x: x1,
        y: y1
      },
      {
        x: x2,
        y: y2
      }
    );
    const secondEdgeCaret = new LineSegment(
      {
        x: x2,
        y: y2
      },
      {
        x: x3,
        y: y3
      }
    );
    const thirdEdgeCaret = new LineSegment(
      {
        x: x3,
        y: y3
      },
      {
        x: x4,
        y: y4
      }
    );
    // const fourthEdgeCaret = new LineSegment(
    //   {
    //     x: x4,
    //     y: y4
    //   },
    //   {
    //     x: x1,
    //     y: y1
    //   }
    // );
    let intersectionPoint = calculateIntersectionPoint(
      firstEdgeCaret,
      rightEdgeQuadLineSegment
    );
    if (
      intersectionPoint &&
      intersectionPoint.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH
    ) {
      return caretQuad;
    }
    intersectionPoint = calculateIntersectionPoint(
      secondEdgeCaret,
      rightEdgeQuadLineSegment
    );
    if (
      intersectionPoint &&
      intersectionPoint.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH
    ) {
      return this.rotateArray(caretQuad, 2);
    }
    intersectionPoint = calculateIntersectionPoint(
      thirdEdgeCaret,
      rightEdgeQuadLineSegment
    );
    if (
      intersectionPoint &&
      intersectionPoint.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH
    ) {
      return this.rotateArray(caretQuad, 4);
    }
    return this.rotateArray(caretQuad, 6);
  }

  rotateArray(arr, index) {
    // Check if the provided index is within the bounds of the array
    if (index < 0 || index >= arr.length) {
      throw new Error("Index out of bounds");
    }

    // Split the array into two parts: from the index to the end, and from the start to the index
    const part1 = arr.slice(index);
    const part2 = arr.slice(0, index);

    // Concatenate the two parts to get the rotated array
    return part1.concat(part2);
  }

  get area() {
    const actualWidth = findDistanceBwPoints(this.topLeft, this.topRight);
    const actualHeight = findDistanceBwPoints(this.topLeft, this.bottomLeft);
    return actualWidth * actualHeight;
  }
}

export default CanvasQuad;
